<script lang="ts" setup>
import { computed, reactive } from 'vue'
import type { ScenarioChatForm_Message_BasicSelectionItem } from '../../types'
import type { Option } from '@/types/app'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import IconClose from '@/components/icons/IconClose.vue'
import Actions from './Actions/Actions.vue'
import { useInternalScenarioChatFormStore } from '../../form/form'
import { truncate } from '@/libs/utilities/truncate'

const props = defineProps<{
  selectedMessageClientOnlyId: string
  index: number
  isDeletable: boolean
  selection: ScenarioChatForm_Message_BasicSelectionItem
}>()

const emit = defineEmits<{
  addNextMessage: []
  deleteSelection: []
}>()

const formStore = reactive(useInternalScenarioChatFormStore())

const handleUpdateLabel = (v: string) => {
  formStore.updateMessage(props.selectedMessageClientOnlyId, draft => {
    if (draft.selections.type !== 'BasicSelections') {
      throw new Error('draft.selections.type is not BasicSelections')
    }

    draft.selections.basicSelections[props.index].label = v
  })
}

const handleClickDeleteSelectionButton = (e: MouseEvent) => {
  e.preventDefault()

  const confirmed = window.confirm('選択肢を削除します。よろしいですか？')

  if (confirmed) {
    emit('deleteSelection')
  }
}

const ADD_NEXT_MESSAGE_SELECTION_VALUE = 'ADD_NEXT_MESSAGE_SELECTION'

const nextChatMessageOptions = computed<Array<Option<string | null>>>(() => {
  const allMessages = Object.values(formStore.formValues.messages)
  const excludeCurrentMessage = allMessages.filter(
    message => message.clientOnlyId !== props.selectedMessageClientOnlyId
  )

  return [
    {
      text: 'なし',
      value: null,
    },
    ...excludeCurrentMessage.map(message => ({
      text: `${message.forUserIndex}. ${truncate(message.body, 20)}`, // あとで関数化する
      value: message.clientOnlyId,
    })),
    {
      text: 'メッセージを追加する',
      value: ADD_NEXT_MESSAGE_SELECTION_VALUE,
    },
  ]
})

const handleSelectionTargetInput = (value: any) => {
  if (value === null) {
    formStore.disconnect({
      sourceMessageClientOnlyId: props.selectedMessageClientOnlyId,
      sourceSelectionClientOnlyId: props.selection.clientOnlyId,
    })
    return
  }

  if (value === ADD_NEXT_MESSAGE_SELECTION_VALUE) {
    emit('addNextMessage')
    return
  }

  formStore.connect({
    sourceMessageClientOnlyId: props.selectedMessageClientOnlyId,
    sourceSelectionClientOnlyId: props.selection.clientOnlyId,
    targetMessageClientOnlyId: value,
  })
}
</script>

<template>
  <div>
    <div :class="$style.selectionHeader">
      <label :class="$style.label">選択肢 {{ index + 1 }}</label>
      <button
        v-if="isDeletable"
        @click="handleClickDeleteSelectionButton"
        :class="$style.deleteSelectionButton"
      >
        <IconClose :class="$style.deleteSelectionButtonIcon" />
      </button>
    </div>
    <div :class="$style.inputWrapper">
      <label :class="$style.label">テキスト</label>
      <DolphinTextInput
        :class="$style.input"
        :modelValue="props.selection.label"
        @update:modelValue="$event => handleUpdateLabel($event as string)"
      />
    </div>
    <div :class="$style.inputWrapper">
      <p :class="$style.label">アクション</p>
      <Actions
        :actions="props.selection.actions"
        :selectedMessageClientOnlyId="props.selectedMessageClientOnlyId"
        :selectionIndex="props.index"
        :class="$style.actions"
      />
    </div>
    <div :class="$style.inputWrapper">
      <p :class="$style.label">次のメッセージ</p>
      <DolphinSelector
        :options="nextChatMessageOptions"
        :modelValue="props.selection.nextScenarioChatMessageClientOnlyId"
        placeholder="なし"
        :class="$style.nextChatMessageSelector"
        @update:modelValue="handleSelectionTargetInput"
      />
    </div>
  </div>
</template>

<style lang="scss" module>
.container {
  background-color: #ffffff;
  border-left: 1px solid #cccccc;
  padding: 32px 16px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.closeButtonWrapper {
  display: flex;
  justify-content: flex-end;
}

.label {
  font-size: 15px;
  font-weight: 500;
}

.body {
  width: 100%;
  box-sizing: border-box;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.4;
  resize: none;
  margin-top: 8px;
}

.selections {
  margin-top: 16px;
}

.setSelectionsButton {
  cursor: pointer;
  color: $color-text-and-object-accent-primary;
  text-decoration: underline;
}

.selectionInner {
  margin-top: 8px;
}

.selectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.deleteSelectionButton {
  display: inline-flex;
  border-radius: 999em;
  padding: 8px;

  &:hover {
    color: $color-caution-accent;
    background-color: $color-background-base;
  }
}

.deleteSelectionButtonIcon {
  width: 16px;
  height: 16px;
}

.textButton {
  cursor: pointer;
  color: $color-text-and-object-accent-primary;
  text-decoration: underline;
}

.actions {
  margin-top: 8px;
}

.inputWrapper {
  margin-top: 24px;
}

.input {
  font-size: 12px;
  margin-top: 8px;
  width: 100%;
}

.nextChatMessageSelector {
  width: 100%;
  margin-top: 8px;
}
</style>
